import {ListItem} from '../../../../../categorization/utils';
import {numberFormatter} from '../../../../../../utils/utils';

export interface ListItemsExtended extends ListItem {
  documentCount: number | null;
  /** Formatted count for ArrTreeView `isShowCountLabel` (e.g. "10,000"). */
  count?: string;
}

export const treeDataSourceSelection = (
  items: any = []
): ListItemsExtended[] => {
  const map = new Map<string, ListItemsExtended>();
  const roots: ListItemsExtended[] = [];

  for (const item of items) {
    const id = String(item.id);
    const parent =
      item.parent != null && item.parent !== '#'
        ? String(item.parent)
        : undefined;
    const documentCount = resolveDocumentCount(item);
    const label = resolveLabel(item, id);

    map.set(id, {
      id,
      label,
      documentCount,
      count:
        documentCount != null && !item.isGroup
          ? numberFormatter.format(documentCount)
          : undefined,
      parent,
      text: item.text ?? item.textWithoutCount ?? label,
      children: [],
      skipParent: item.isGroup ?? false,
    });
  }

  for (const item of items) {
    const id = String(item.id);
    const parent =
      item.parent != null && item.parent !== '#'
        ? String(item.parent)
        : undefined;
    const node = map.get(id);
    if (!node) continue;

    if (parent && map.has(parent)) {
      map.get(parent)!.skipParent = true;
      map.get(parent)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }

  // Header / parent rows: keep documentCount for selection math, hide count label
  for (const node of map.values()) {
    if (node.skipParent || (node.children?.length ?? 0) > 0) {
      node.count = undefined;
    }
  }

  // Disable selectable leaf nodes with zero documents (AVR-23031)
  for (const node of map.values()) {
    const isLeaf = (node.children?.length ?? 0) === 0;
    if (isLeaf && node.documentCount === 0) {
      node.disabled = true;
    }
  }

  return roots;
};

/**
 * Count is embedded in `text` (e.g. "0001Tag1 (5)") for tags, folders,
 * and saved searches. Prefer that over `documentCount`, which is often 0.
 */
const resolveDocumentCount = (item: {
  documentCount?: number | null;
  text?: string;
}): number | null => {
  const fromText = getCountFromText(item.text);
  if (fromText != null) {
    return fromText;
  }
  if (
    typeof item.documentCount === 'number' &&
    !Number.isNaN(item.documentCount)
  ) {
    return item.documentCount;
  }
  return null;
};

const resolveLabel = (
  item: {textWithoutCount?: string; text?: string; label?: string},
  fallbackId: string
): string => {
  if (item.textWithoutCount?.trim()) {
    return item.textWithoutCount.trim();
  }
  const fromText = getLabelFromText(item.text);
  if (fromText) {
    return fromText;
  }
  if (item.label?.trim()) {
    return item.label.trim();
  }
  return fallbackId;
};

const getLabelFromText = (text = '') => {
  return String(text).replace(/\s{0,20}\([\d,]{1,20}\)\s{0,20}$/, '');
};

const getCountFromText = (text = ''): number | null => {
  const match = String(text).match(/\(([\d,]+)\)\s*$/);
  if (!match) {
    return null;
  }
  const parsed = Number(match[1].replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
};
